// 18+ self-attestation status + writer for the post-auth gate (ProtectedRoute).
// The value rarely changes within a session, so it's cached with staleTime:Infinity
// and updated in-place after the user attests — no refetch, no flash.
//
// This is a SELF-DECLARATION with NO enforcement weight (the real gate is Persona
// KYC at the money moment). It exists to record that the user attested and when.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const key = (userId: string | undefined) => ['attestation-18plus', userId] as const;

/**
 * Whether the current user has recorded the 18+ self-attestation.
 * Returns `false` for a missing/never-attested profile (→ show the gate).
 * On a fetch error we deliberately leave `data` undefined so the caller keeps
 * showing the gate (fail-safe: never wave an unconfirmed user into the app).
 */
export function useAttestation18Plus(userId: string | undefined) {
  return useQuery({
    queryKey: key(userId),
    enabled: !!userId,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<boolean> => {
      // `attested_18_plus` isn't in the generated types yet; cast the builder.
      const { data, error } = await (supabase.from('profiles') as any)
        .select('attested_18_plus')
        .eq('id', userId)
        .maybeSingle();
      if (error) throw error;
      return Boolean(data?.attested_18_plus);
    },
  });
}

/**
 * Record the attestation via the SECURITY DEFINER RPC (server-stamped, idempotent),
 * then optimistically flip the cached status so ProtectedRoute renders the app
 * without a refetch. Throws on failure so the gate can surface a retry.
 */
export function useRecord18PlusAttestation(userId: string | undefined) {
  const qc = useQueryClient();
  return async () => {
    // The RPC returns TRUE only if a row was actually written. FALSE means the
    // profile row doesn't exist yet (mid-signup race) — refuse to advance so the
    // user retries a moment later rather than "attesting into nothing" and being
    // re-prompted forever with no record. Only flip the cache on a confirmed write.
    const { data, error } = await (supabase as any).rpc('record_18plus_attestation');
    if (error) throw error;
    if (data !== true) throw new Error('attestation-not-recorded');
    qc.setQueryData(key(userId), true);
  };
}
